import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Space from "antd/es/space";
import { ColumnType } from "antd/es/table";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import { Activity } from "@mapmycustomers/shared/types/entity";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import { EntityTypeShapedIcon } from "@mapmycustomers/ui";
import { TextField } from "@mapmycustomers/ui";

import ActivityAssignee from "@app/component/activity/ActivityAssignee";
import EmailMetrics from "@app/component/EmailMetrics";
import EmailsMetrics from "@app/component/EmailsMetrics";
import Table from "@app/component/Table";
import { showEntityView } from "@app/store/entityView/actions";
import EntityViewPayload from "@app/store/entityView/EntityViewPayload";
import { areEmailMetricsVisible } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import compareEmailEngagement from "@app/util/email/compareEmailEngagement";
import filterActivitiesByRecipient from "@app/util/email/filterActivitiesByRecipient";
import getEmailLogRecipientName from "@app/util/email/getEmailLogRecipientName";

import styles from "./ActivitiesView.module.scss";
import ActivityView from "./ActivityView";
import { loadAnnotationData } from "./store/actions";
import { getActivities } from "./store/selectors";

const messages = defineMessages({
  metricsTitle: {
    id: "component.activityAnnotation.activities.metricsTitle",
    defaultMessage: "Metrics",
    description: "Email activities metrics title",
  },
  recipientsTitle: {
    id: "component.activityAnnotation.activities.recipientsTitle",
    defaultMessage: "Recipients ({count})",
    description: "Email activities recipients title",
  },
  searchPlaceholder: {
    id: "component.activityAnnotation.activities.searchPlaceholder",
    defaultMessage: "Search recipients",
    description: "Email activities search placeholder",
  },
});

interface ActivitiesViewProps {
  allowSendViaEmailClient?: boolean;
  allowSendViaMmcEmail?: boolean;
  onChange?: (activity: Activity) => void;
}

const ActivitiesView = ({
  activities,
  allowSendViaEmailClient,
  allowSendViaMmcEmail,
  onChange,
  onLoadAnnotationData,
  onShowEntityView,
  trackEmails,
}: ActivitiesViewProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();

  const [searchText, setSearchText] = useState("");
  const [filteredActivities, setFilteredActivities] = useState(activities);
  const [selectedActivityId, setSelectedActivityId] = useState<Activity["id"] | undefined>();

  const emailMetrics = useMemo(() => {
    const emailLogs = activities.map((activity) => activity.emailLog!);
    return {
      clicked: emailLogs.filter(({ linksOpened }) => linksOpened?.length).length,
      opened: emailLogs.filter(({ opened }) => opened).length,
      total: emailLogs.length,
    };
  }, [activities]);

  const columns: ColumnType<Activity>[] = useMemo(() => {
    const columns: ColumnType<Activity>[] = [];

    columns.push({
      defaultSortOrder: "ascend",
      key: "recipients",
      render: (_, activity) => getEmailLogRecipientName(activity),
      sorter: (a, b) => getEmailLogRecipientName(a).localeCompare(getEmailLogRecipientName(b)),
      title: (
        <Space>
          <EntityTypeShapedIcon
            className={styles.entityTypeIconContainer}
            entityType={activities[0].emailLog!.entityType}
            iconClassName={styles.entityTypeIcon}
          />
          <span>{intl.formatMessage(messages.recipientsTitle, { count: activities.length })}</span>
        </Space>
      ),
    });

    if (trackEmails) {
      columns.push({
        key: "metrics",
        render: (_, activity) => (
          <EmailMetrics email={activity.emailLog} size="small" transparent />
        ),
        sorter: compareEmailEngagement,
        title: intl.formatMessage(messages.metricsTitle),
      });
    }

    return columns;
  }, [intl, activities, trackEmails]);

  const handleSearch = useDebouncedCallback(
    [
      (searchText: string) => {
        setFilteredActivities(filterActivitiesByRecipient(activities, searchText));
      },
    ],
    [setFilteredActivities]
  );

  const handleActivitySelect = useCallback(
    (activity: Activity) => {
      setSelectedActivityId(activity.id);
    },
    [setSelectedActivityId]
  );

  const handleEdit = useCallback(
    (payload: EntityViewPayload) => {
      onShowEntityView({ ...payload, edit: true });
    },
    [onShowEntityView]
  );

  const rowEventHandlers = useCallback(
    (activity: Activity) => ({
      onClick: () => {
        handleActivitySelect(activity);
      },
    }),
    [handleActivitySelect]
  );

  const getRowClassName = useCallback(
    (activity: Activity) => {
      return cn(styles.row, activity.id === selectedActivityId ? styles.selectedRow : undefined);
    },
    [selectedActivityId]
  );

  useEffect(() => {
    if (
      activities.length &&
      (!selectedActivityId || !activities.some((activity) => activity.id === selectedActivityId))
    ) {
      setSelectedActivityId(activities[0].id);
    }
  }, [activities, selectedActivityId]);

  useEffect(() => {
    if (selectedActivityId) {
      onLoadAnnotationData(selectedActivityId);
    }
  }, [selectedActivityId, onLoadAnnotationData]);

  useEffect(() => {
    const activitiesMap = new Map(activities.map((activity) => [activity.id, activity]));
    setFilteredActivities((filteredActivities) =>
      filteredActivities
        .map((filteredActivity) => activitiesMap.get(filteredActivity.id))
        .filter(isDefined)
    );
  }, [activities]);

  useEffect(() => {
    handleSearch(searchText);
  }, [handleSearch, searchText]);

  return (
    <>
      <div className={styles.container}>
        <Space>
          <EmailsMetrics
            clicked={emailMetrics.clicked}
            opened={emailMetrics.opened}
            total={emailMetrics.total}
          />
          <ActivityAssignee activity={activities[0]} />
        </Space>
        <div className={styles.panelsContainer}>
          <div className={styles.activitiesPanel}>
            <TextField
              onChange={setSearchText}
              placeholder={intl.formatMessage(messages.searchPlaceholder)}
              value={searchText}
            />
            <Table<Activity>
              className={styles.table}
              columns={columns}
              dataSource={filteredActivities}
              onRow={rowEventHandlers}
              pagination={false}
              rowClassName={getRowClassName}
              rowKey="id"
              scrollable
              showBorder
              size="small"
            />
          </div>
          <div className={styles.activityPanel}>
            <ActivityView
              allowSendViaEmailClient={allowSendViaEmailClient}
              allowSendViaMmcEmail={allowSendViaMmcEmail}
              className={styles.activityView}
              onChange={onChange}
              onEdit={handleEdit}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export const mapStateToProps = (state: RootState) => ({
  activities: getActivities(state),
  trackEmails: areEmailMetricsVisible(state),
});

const mapDispatchToProps = {
  onLoadAnnotationData: loadAnnotationData.request,
  onShowEntityView: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(ActivitiesView);
